import React, { useState } from 'react';

const SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: [
      {
        q: 'Setting up your profile',
        a: 'Go to Settings (gear icon in the bottom nav) and fill in your name, calorie goal, and optional macro targets for protein, carbs, and fat. These goals appear on your dashboard so you can track daily progress.'
      },
      {
        q: 'Using the TDEE calculator',
        a: 'In Settings under Body Stats, enter your sex, age, weight, height, activity level, and goal (lose/maintain/gain). Tap "Calculate Suggested Calories" to get a personalized daily calorie target based on the Mifflin-St Jeor equation. The calculator also suggests macro goals using a 30/40/30 split (30% protein, 40% carbs, 30% fat). Tap "Use Suggested Macros" to auto-fill all four goals at once.'
      },
      {
        q: 'What do the macro percentages mean?',
        a: 'The suggested split allocates 30% of your calories to protein (÷4 for grams), 40% to carbs (÷4 for grams), and 30% to fat (÷9 for grams). This is a balanced starting point — you can manually adjust the values in Settings after applying them.'
      }
    ]
  },
  {
    id: 'searching-logging',
    title: 'Searching & Logging Food',
    content: [
      {
        q: 'How to search for foods',
        a: 'Tap the Search tab in the bottom nav and type a food name. Results appear as you type (with a brief delay). Tap any result to see its full nutrition info and add it to your log.'
      },
      {
        q: 'Branded vs USDA Standard results',
        a: 'Search results show a label under each food name. "USDA Standard" items are generic reference foods (like "chicken breast, raw") with per-100g nutrition data. Branded items show the manufacturer name in cyan and have nutrition for the specific product\'s serving size. Use USDA Standard for home-cooked meals, branded for packaged foods.'
      },
      {
        q: 'Adjusting serving size and quantity',
        a: 'After selecting a food, use the +/- buttons or type directly to set the quantity (number of servings). The calorie and macro numbers update in real-time. For example, if a serving is 150g and you ate 300g, set quantity to 2.'
      },
      {
        q: 'Picking a meal type and date',
        a: 'Choose from Breakfast, Lunch, Dinner, or Snack using the meal buttons. The date defaults to today but you can tap the date field to log food for a past or future date.'
      },
      {
        q: 'Quick-adding recent foods',
        a: 'On the Dashboard, the Quick Add section shows your most recently logged foods as chips. Tap any chip to instantly re-add that food with the same nutrition info (quantity 1). Great for meals you eat regularly.'
      }
    ]
  },
  {
    id: 'tracking-progress',
    title: 'Tracking Your Progress',
    content: [
      {
        q: 'Reading the dashboard',
        a: 'The dashboard shows today\'s progress: a calorie progress bar (green = under goal, orange = near goal, red = over), your current vs goal calories, and macro cards showing protein, carbs, and fat with their targets. Below that is your complete food log for today.'
      },
      {
        q: 'Viewing and editing your food log',
        a: 'Tap the Log tab to see your food entries grouped by meal. Use the arrows to navigate between dates. Tap any entry\'s quantity to edit it inline. To delete an entry, tap the X button once to see "Sure?", then tap again to confirm.'
      },
      {
        q: 'Using the history page',
        a: 'From the Dashboard, tap "View History" to see a bar chart of your daily calories over the past 7 or 30 days. Bars are color-coded: green (under 80% of goal), orange (80-100%), red (over goal). Tap any day to jump to that date\'s food log.'
      }
    ]
  },
  {
    id: 'tips',
    title: 'Tips for Accuracy',
    content: [
      {
        q: 'Log as you eat',
        a: 'The most accurate food logs are made in real-time. Logging right after a meal takes seconds and prevents forgetting items later. Use the quick-add chips on the dashboard for foods you eat often.'
      },
      {
        q: 'Use generic USDA items for home-cooked meals',
        a: 'When cooking from scratch, search for individual ingredients (e.g., "chicken breast raw", "olive oil", "brown rice cooked") and log each one. USDA Standard items give you reliable per-gram nutrition data.'
      },
      {
        q: 'Use branded items for packaged foods',
        a: 'For anything with a nutrition label — snacks, drinks, frozen meals — search for the specific brand. The serving size in the search results matches what\'s on the package, so the calories will be accurate.'
      },
      {
        q: 'Weigh your food when possible',
        a: 'A kitchen scale makes a huge difference in accuracy. Eyeballing portions is notoriously unreliable — studies show people underestimate by 30-50%. Even a cheap digital scale helps you log the right quantity.'
      }
    ]
  }
];

function Help() {
  const [openSections, setOpenSections] = useState(new Set(['getting-started']));

  const toggle = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="page help-page">
      <div className="help-header">
        <h2>Help & Guide</h2>
        <p className="text-muted">Everything you need to know about CalorieTrack™</p>
      </div>

      {SECTIONS.map(section => {
        const isOpen = openSections.has(section.id);
        return (
          <div key={section.id} className="help-section">
            <button
              className={`help-section-toggle ${isOpen ? 'open' : ''}`}
              onClick={() => toggle(section.id)}
            >
              <span className="help-section-title">{section.title}</span>
              <span className="help-section-arrow">{isOpen ? '\u2212' : '+'}</span>
            </button>
            <div className={`help-section-body ${isOpen ? 'expanded' : ''}`}>
              <div className="help-section-inner">
                {section.content.map((item, i) => (
                  <div key={i} className="help-item">
                    <h4 className="help-item-q">{item.q}</h4>
                    <p className="help-item-a">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Help;
